import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { notificationsApi } from '../../../api/notifications.js';
import { ApiError } from '../../../api/client.js';
import { Card } from '../../../components/ui/Card.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Switch } from '../../../components/ui/Switch.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { TagInput } from '../../../components/ui/TagInput.jsx';
import { t } from '../../../lib/i18n.js';

export function ConfigTab({ canManage }) {
  const queryClient = useQueryClient();
  const configQuery = useQuery({ queryKey: ['notifications', 'config'], queryFn: notificationsApi.getConfig });

  const [form, setForm] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    if (configQuery.data) {
      const c = configQuery.data.custom || {};
      setForm({
        enabled: configQuery.data.enabled,
        provider: configQuery.data.provider || (configQuery.data.providers && configQuery.data.providers[0]?.id) || '',
        senderEmail: configQuery.data.senderEmail || '',
        senderName: configQuery.data.senderName || '',
        fallbackRecipients: configQuery.data.fallbackRecipients || [],
        appBaseUrl: configQuery.data.appBaseUrl || '',
        logoUrl: configQuery.data.logoUrl || '',
        custom: {
          endpoint: c.endpoint || '',
          method: c.method || 'POST',
          bodyTemplate: c.bodyTemplate || '',
          messageIdPath: c.messageIdPath || ''
        },
        headersText: JSON.stringify(c.headers || { 'content-type': 'application/json' }, null, 2)
      });
    }
  }, [configQuery.data]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['notifications', 'config'] });

  const saveMutation = useMutation({
    mutationFn: (body) => notificationsApi.updateConfig(body),
    onSuccess: () => { toast.success('Configurazione salvata.'); setApiKey(''); invalidate(); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const testMutation = useMutation({
    mutationFn: () => notificationsApi.test({ to: testTo.trim() }),
    onSuccess: () => toast.success('Email di prova inviata.'),
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  if (!form) return <Card><p className="text-sm text-ink-muted">Caricamento...</p></Card>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCustom = (k, v) => setForm((f) => ({ ...f, custom: { ...f.custom, [k]: v } }));

  const onSave = () => {
    const { headersText, custom, ...rest } = form;
    const body = { ...rest };
    if (apiKey.trim()) body.apiKey = apiKey.trim();
    if (form.provider === 'custom') {
      let headers;
      try { headers = JSON.parse(headersText || '{}'); }
      catch { toast.error('Header non in formato JSON valido.'); return; }
      body.custom = { ...custom, headers };
    }
    saveMutation.mutate(body);
  };

  const configured = configQuery.data?.apiKeyConfigured;
  const providers = configQuery.data?.providers || [];

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <header className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-ink">Provider email</h2>
            <p className="text-xs text-ink-muted">Le email vengono inviate tramite il provider transazionale selezionato.</p>
          </div>
          <Badge tone={configured ? 'success' : 'warning'}>{configured ? 'API key configurata' : 'API key mancante'}</Badge>
        </header>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2 flex items-center justify-between rounded-lg border border-border bg-surface p-3">
            <div>
              <p className="text-sm font-medium text-ink">Notifiche email attive</p>
              <p className="text-xs text-ink-muted">Interruttore generale: se spento non viene inviata alcuna email.</p>
            </div>
            <Switch checked={form.enabled} disabled={!canManage} onChange={(v) => set('enabled', v)} />
          </div>

          <Select label="Provider" value={form.provider} disabled={!canManage} onChange={(e) => set('provider', e.target.value)} hint="Cambiabile in futuro senza toccare le regole o i template.">
            {providers.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </Select>
          <Input
            label="API key del provider"
            type="password"
            autoComplete="off"
            placeholder={configured ? '•••••••• (lasciare vuoto per non cambiare)' : 'Incolla la API key'}
            value={apiKey}
            disabled={!canManage}
            onChange={(e) => setApiKey(e.target.value)}
            hint="Salvata cifrata nel database con la master key. Non viene mai mostrata in chiaro."
          />
          <Input label="Email mittente" type="email" placeholder="noreply@tuodominio.it" value={form.senderEmail} disabled={!canManage} onChange={(e) => set('senderEmail', e.target.value)} hint="Deve essere un mittente verificato presso il provider." />
          <Input label="Nome mittente" value={form.senderName} disabled={!canManage} onChange={(e) => set('senderName', e.target.value)} />
          <Input label="Base URL app" placeholder="https://..." value={form.appBaseUrl} disabled={!canManage} onChange={(e) => set('appBaseUrl', e.target.value)} hint="Usato per i link dentro le email." />
          <Input label="URL logo (email)" placeholder="https://.../logo.png" value={form.logoUrl} disabled={!canManage} onChange={(e) => set('logoUrl', e.target.value)} hint="Mostrato in cima alle email. Vuoto = usa il favicon dell'app." />
          <div className="sm:col-span-2">
            <TagInput label="Destinatari di fallback" value={form.fallbackRecipients} onChange={(v) => set('fallbackRecipients', v)} lowercase placeholder="email e Invio" />
            <p className="mt-1 text-xs text-ink-muted">Usati quando una regola non risolve nessun destinatario specifico.</p>
          </div>
        </div>

        {form.provider === 'custom' && (
          <div className="mt-5 rounded-lg border border-border bg-surface p-4">
            <p className="mb-1 text-sm font-medium text-ink">Configurazione provider HTTP</p>
            <p className="mb-3 text-xs text-ink-muted">Definisci come chiamare l'API. La API key qui sopra viene iniettata dove scrivi <code>{'{{apiKey}}'}</code> negli header.</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <Input label="Endpoint" placeholder="https://api.provider.com/v1/send" value={form.custom.endpoint} disabled={!canManage} onChange={(e) => setCustom('endpoint', e.target.value)} />
              </div>
              <Select label="Metodo" value={form.custom.method} disabled={!canManage} onChange={(e) => setCustom('method', e.target.value)}>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
              </Select>
              <div className="sm:col-span-3">
                <Textarea label="Header (JSON)" rows={3} value={form.headersText} disabled={!canManage} onChange={(e) => set('headersText', e.target.value)} hint='Es. { "Authorization": "Bearer {{apiKey}}", "content-type": "application/json" }' />
              </div>
              <div className="sm:col-span-3">
                <Textarea label="Template body (JSON)" rows={8} value={form.custom.bodyTemplate} disabled={!canManage} onChange={(e) => setCustom('bodyTemplate', e.target.value)} />
                <p className="mt-1 text-xs text-ink-muted">Segnaposto: <code className="font-mono">{'{{from}} {{fromName}} {{recipients}} {{toEmail}} {{subject}} {{html}} {{text}}'}</code>. Gli scalari sono gia codificati come stringhe JSON, <code className="font-mono">{'{{recipients}}'}</code> e un array <code className="font-mono">[{'{'}"email":"..."{'}'}]</code>.</p>
              </div>
              <Input label="Percorso del messageId nella risposta" placeholder="es. id oppure data.id" value={form.custom.messageIdPath} disabled={!canManage} onChange={(e) => setCustom('messageIdPath', e.target.value)} />
            </div>
          </div>
        )}

        {canManage && (
          <div className="mt-5 flex justify-end">
            <Button onClick={onSave} loading={saveMutation.isPending}>Salva configurazione</Button>
          </div>
        )}
      </Card>

      {canManage && (
        <Card>
          <header className="mb-3">
            <h2 className="text-base font-semibold text-ink">Invio di prova</h2>
            <p className="text-xs text-ink-muted">Verifica che la API key e il mittente funzionino.</p>
          </header>
          <div className="flex flex-wrap items-end gap-2">
            <Input className="w-72" label="Invia a" type="email" placeholder="tu@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
            <Button variant="secondary" onClick={() => testMutation.mutate()} loading={testMutation.isPending} disabled={!testTo.trim() || !configured}>Invia prova</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
